/**
 * Turning ELM327 text output back into bytes.
 *
 * The adapter speaks lines of ASCII hex terminated by CR, and finishes every
 * exchange with a '>' prompt. What those lines look like depends on how the
 * adapter is configured and on whether the ECU replied in one CAN frame or
 * several, so this module normalises all of the shapes we might see.
 */
import { PID_BY_NUMBER } from '../pids/registry.js';

/** Adapter-level replies that mean "no useful data", not a protocol payload. */
const ERROR_TOKENS = [
  'NO DATA',
  'STOPPED',
  'UNABLE TO CONNECT',
  'CAN ERROR',
  'BUS INIT',
  'BUS ERROR',
  'BUS BUSY',
  'DATA ERROR',
  'BUFFER FULL',
  'FB ERROR',
  'LV RESET',
  'ACT ALERT',
  'ERR',
  'SEARCHING',
  'UNABLE',
  '?',
];

export class ObdError extends Error {
  constructor(message: string, readonly kind: 'no-data' | 'protocol' | 'unsupported' | 'timeout' | 'adapter') {
    super(message);
    this.name = 'ObdError';
  }
}

/** Split raw adapter output into trimmed, uppercase, space-free lines. */
export function cleanLines(raw: string): string[] {
  return raw
    .replace(/>/g, '')
    .split(/[\r\n]+/)
    .map((l) => l.replace(/\s+/g, '').toUpperCase())
    .filter((l) => l.length > 0);
}

/** True if the line is an adapter status message rather than hex data. */
export function isErrorLine(line: string): boolean {
  const bare = line.replace(/[^A-Z?]/g, '');
  return ERROR_TOKENS.some((t) => bare.includes(t.replace(/[^A-Z?]/g, '')));
}

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/[^0-9A-Fa-f]/g, '');
  const n = clean.length >> 1;
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (const byte of bytes) s += byte.toString(16).padStart(2, '0').toUpperCase();
  return s;
}

/**
 * Reassemble one logical ECU payload from the adapter's lines.
 *
 * Three shapes turn up in practice:
 *   single frame   ->  "4105 5A"                    (one line of hex)
 *   ISO-TP multi   ->  "014", "0:4100BE3F", "1:.."  (length, then indexed rows)
 *   several ECUs   ->  two or more independent single frames
 *
 * Returns one hex string per distinct response. Frame-index rows are stitched
 * together and truncated to the declared length.
 */
export function assemblePayloads(lines: string[]): string[] {
  const out: string[] = [];
  let pending: { declaredBytes: number; parts: Map<number, string> } | null = null;

  const flush = (): void => {
    if (!pending) return;
    const ordered = [...pending.parts.entries()].sort((a, c) => a[0] - c[0]).map(([, v]) => v);
    // The first row of an ISO-TP sequence carries the 2-byte PCI already
    // stripped by the adapter, so simple concatenation is correct here.
    const joined = ordered.join('');
    out.push(joined.slice(0, pending.declaredBytes * 2));
    pending = null;
  };

  for (const line of lines) {
    if (isErrorLine(line)) continue;

    // A bare 3-hex-digit line announces the byte count of a multi-frame reply.
    if (/^[0-9A-F]{3}$/.test(line)) {
      flush();
      pending = { declaredBytes: parseInt(line, 16), parts: new Map() };
      continue;
    }

    const indexed = /^([0-9A-F]):([0-9A-F]+)$/.exec(line);
    if (indexed) {
      const idx = parseInt(indexed[1] as string, 16);
      const data = indexed[2] as string;
      if (!pending) pending = { declaredBytes: 0xffff, parts: new Map() };
      pending.parts.set(idx, data);
      continue;
    }

    if (!/^[0-9A-F]+$/.test(line) || line.length < 4) continue;
    flush();
    out.push(line);
  }
  flush();
  return out;
}

/**
 * Strip a CAN/ISO header from a payload if one is present.
 *
 * We normally run with headers off, but a few adapters ignore ATH0 and some
 * flows deliberately turn them on. A mode 0x01 reply always begins 0x41, so if
 * the payload does not start there we look for it a few bytes in.
 */
export function stripHeader(hex: string, expectMode: number): string {
  const wanted = (expectMode + 0x40).toString(16).padStart(2, '0').toUpperCase();
  if (hex.startsWith(wanted)) return hex;
  // 11-bit header is 3 nibbles, 29-bit is 8; both may be followed by a length byte.
  for (const skipNibbles of [4, 6, 8, 10, 16, 18]) {
    if (hex.length > skipNibbles && hex.substr(skipNibbles, 2) === wanted) {
      return hex.slice(skipNibbles);
    }
  }
  return hex;
}

export interface ParsedFrame {
  /** Data bytes per PID, keyed by PID number. */
  values: Map<number, Uint8Array>;
  /** PIDs that were requested but did not appear in the reply. */
  missing: number[];
}

/**
 * Walk a mode 0x01 payload, which is 0x41 followed by (pid, data...) repeated.
 *
 * Multi-PID requests come back concatenated with no separators, so the only way
 * to split them is to know each PID's payload length from the registry. Any PID
 * we do not recognise ends the walk, since we can no longer find the boundary.
 */
export function parseMode01(payloads: string[], requested: number[]): ParsedFrame {
  const values = new Map<number, Uint8Array>();

  for (const payload of payloads) {
    const hex = stripHeader(payload, 0x01);
    if (!hex.startsWith('41')) continue;
    const bytes = hexToBytes(hex);
    let i = 1;
    while (i < bytes.length) {
      const pid = bytes[i];
      if (pid === undefined) break;
      const def = PID_BY_NUMBER.get(pid);
      // An unknown PID means we cannot know how many bytes to skip. If it is the
      // only thing we asked for, hand over the remainder; otherwise stop.
      if (!def) {
        if (requested.length === 1 && requested[0] === pid) {
          values.set(pid, bytes.slice(i + 1));
        }
        break;
      }
      const data = bytes.slice(i + 1, i + 1 + def.bytes);
      if (data.length < def.bytes) break;
      values.set(pid, data);
      i += 1 + def.bytes;
    }
  }

  return { values, missing: requested.filter((p) => !values.has(p)) };
}

/**
 * The support bitmap for `base` as a 32-bit number, OR-ed over every ECU
 * that answered. Which ECU's reply arrives last varies between requests, so
 * taking any single one makes the supported list change between connections.
 */
export function mergeSupportBitmaps(payloads: string[], base: number): number | undefined {
  let merged: number | undefined;
  for (const payload of payloads) {
    const hex = stripHeader(payload, 0x01);
    if (!hex.startsWith('41')) continue;
    const bytes = hexToBytes(hex);
    if (bytes[1] !== base || bytes.length < 6) continue;
    const value = (((bytes[2] ?? 0) << 24) | ((bytes[3] ?? 0) << 16) | ((bytes[4] ?? 0) << 8) | (bytes[5] ?? 0)) >>> 0;
    merged = ((merged ?? 0) | value) >>> 0;
  }
  return merged;
}

/** Parse a support bitmap reply (PIDs 0x00, 0x20, 0x40, ...) into PID numbers. */
export function parseSupportBitmap(base: number, data: Uint8Array): number[] {
  const supported: number[] = [];
  for (let byteIdx = 0; byteIdx < 4; byteIdx++) {
    const byte = data[byteIdx] ?? 0;
    for (let bit = 0; bit < 8; bit++) {
      // Bit 7 of byte 0 is the PID immediately after the bitmap's own number.
      if (byte & (0x80 >> bit)) supported.push(base + byteIdx * 8 + bit + 1);
    }
  }
  return supported;
}
